// useSharedAssessments.js

import { useState, useEffect } from 'react';
import axios from 'axios';

export function useSharedAssessments(apiUrl, sampleId, options = {}) {
    const [sharedAssessments, setSharedAssessments] = useState([]);
    const [peerAssessments, setPeerAssessments] = useState([]);
    const [referenceAssessments, setReferenceAssessments] = useState([]);
    const [excludedAssessments, setExcludedAssessments] = useState([]);
    const [ambiguousReferenceAssessments, setAmbiguousReferenceAssessments] = useState([]);
    const [pvqdContextObservations, setPvqdContextObservations] = useState([]);
    const [directCompatibleEvidence, setDirectCompatibleEvidence] = useState([]);
    const [governedPvqdReference, setGovernedPvqdReference] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const evaluationToolId = options?.evaluationToolId || null;
    const protocolKey = options?.protocolKey || null;
    const protocolVersion = options?.protocolVersion || null;
    const hasPvqdReferenceContract = Boolean(options?.pvqdReferenceContract);
    const pvqdReferenceContractKey = options?.pvqdReferenceContract?.key;
    const pvqdReferenceContractVersion = options?.pvqdReferenceContract?.version;

    useEffect(() => {
        const fetchSharedAssessments = async () => {
            if (!sampleId) {
                console.warn("⚠️ [useSharedAssessments] No sample ID provided.");
                setLoading(false);
                return;
            }

            try {
                const params = new URLSearchParams({
                    sampleId,
                });

                if (evaluationToolId) {
                    params.set("evaluationTool", evaluationToolId);
                }
                if (protocolKey || protocolVersion) {
                    if (protocolKey) params.set("protocolKey", protocolKey);
                    if (protocolVersion) params.set("protocolVersion", protocolVersion);
                }
                if (hasPvqdReferenceContract) {
                    params.set("pvqdReferenceContractKey", pvqdReferenceContractKey);
                    params.set("pvqdReferenceContractVersion", pvqdReferenceContractVersion);
                }

                const response = await axios.get(
                    `${apiUrl}/voice-assessments?${params.toString()}`
                );
                const governedClassification = response.data.feedbackClassification || null;
                const directEvidenceResults = governedClassification?.directCompatible || [];
                const peerResults = governedClassification
                    ? directEvidenceResults.map(result => result.assessment)
                    : response.data.peerAssessments || response.data.data || [];
                const referenceResults = response.data.referenceAssessments || [];
                const excludedResults = response.data.excludedAssessments || [];
                const ambiguousReferenceResults =
                    response.data.ambiguousReferenceAssessments || [];
                const pvqdContextResults = governedClassification?.pvqdContextObservations || [];

                setPeerAssessments(peerResults);
                setReferenceAssessments(referenceResults);
                setExcludedAssessments(excludedResults);
                setAmbiguousReferenceAssessments(ambiguousReferenceResults);
                setPvqdContextObservations(pvqdContextResults);
                setDirectCompatibleEvidence(directEvidenceResults);
                setGovernedPvqdReference(response.data.governedPvqdReference || null);
                setSharedAssessments(peerResults);
                console.log("✅ [useSharedAssessments] Shared assessments fetched:", response.data.data);
            } catch (err) {
                console.error("⚠️ [useSharedAssessments] Error fetching shared assessments:", err);
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        fetchSharedAssessments();
    }, [apiUrl, sampleId, evaluationToolId, protocolKey, protocolVersion,
        hasPvqdReferenceContract, pvqdReferenceContractKey, pvqdReferenceContractVersion]);

    return {
        sharedAssessments,
        peerAssessments,
        referenceAssessments,
        excludedAssessments,
        ambiguousReferenceAssessments,
        pvqdContextObservations,
        directCompatibleEvidence,
        governedPvqdReference,
        loading,
        error
    };
}
